import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';

export default function NetWorthTracker() {
  const { user } = useAuth();
  const [assets, setAssets] = useState([{ name: 'Cash', value: 0 }, { name: 'Investments', value: 0 }, { name: 'Property', value: 0 }]);
  const [liabilities, setLiabilities] = useState([{ name: 'Loans', value: 0 }, { name: 'Credit Cards', value: 0 }, { name: 'Mortgage', value: 0 }]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [incRes, expRes] = await Promise.all([api.get('/income'), api.get('/expenses')]);
      const incomes = incRes.data?.data || incRes.data || [];
      const expenses = expRes.data?.data || expRes.data || [];
      const totalIncome = incomes.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
      setAssets(prev => prev.map(a => a.name === 'Cash' ? { ...a, value: totalIncome * 0.3 } : a));
    } catch (e) { console.log('Using default values'); }
    finally { setLoading(false); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: user?.currency || 'USD' }).format(v || 0);
  const totalAssets = assets.reduce((s, a) => s + a.value, 0);
  const totalLiabilities = liabilities.reduce((s, l) => s + l.value, 0);
  const netWorth = totalAssets - totalLiabilities;

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Net Worth Tracker</h1>
        <p className="text-gray-500">Track your overall financial health</p>
      </div>

      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white text-center">
        <p className="text-indigo-200 text-sm uppercase tracking-wide">Net Worth</p>
        <p className="text-5xl font-bold mt-2">{formatCurrency(netWorth)}</p>
        <div className="flex justify-center gap-8 mt-4">
          <div><p className="text-indigo-200 text-xs">Assets</p><p className="font-bold">{formatCurrency(totalAssets)}</p></div>
          <div><p className="text-indigo-200 text-xs">Liabilities</p><p className="font-bold">{formatCurrency(totalLiabilities)}</p></div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-green-700 mb-4">💰 Assets</h3>
          {assets.map((a, i) => (
            <div key={i} className="flex justify-between items-center py-3 border-b border-gray-100 last:border-0">
              <span className="text-gray-700">{a.name}</span>
              <input type="number" value={a.value} onChange={e => { const na = [...assets]; na[i].value = parseFloat(e.target.value) || 0; setAssets(na); }}
                className="w-32 text-right px-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500" />
            </div>
          ))}
          <p className="text-right font-bold text-green-600 mt-3">{formatCurrency(totalAssets)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-red-700 mb-4">💳 Liabilities</h3>
          {liabilities.map((l, i) => (
            <div key={i} className="flex justify-between items-center py-3 border-b border-gray-100 last:border-0">
              <span className="text-gray-700">{l.name}</span>
              <input type="number" value={l.value} onChange={e => { const nl = [...liabilities]; nl[i].value = parseFloat(e.target.value) || 0; setLiabilities(nl); }}
                className="w-32 text-right px-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500" />
            </div>
          ))}
          <p className="text-right font-bold text-red-600 mt-3">{formatCurrency(totalLiabilities)}</p>
        </div>
      </div>
    </div>
  );
}
